/*
  plp-card-reveal.js — collection PLP card scroll-reveal.

  Cards in the collection grid start hidden (CSS under html.js) and fade +
  grow into place as they enter the viewport. Cards in the same intersection
  batch stagger relative to each other in visual reading order; the stagger
  counter resets after a scroll-idle gap so a card scrolled to in isolation
  appears immediately instead of inheriting a long cumulative delay.

  Nothing is revealed at load. Whichever engine owns the grid calls observe():
  plp-searchanise-controller for the cards it builds, and for the SSR cards it
  leaves in place when it declines the page. Revealing here as well would
  double-stagger the healthy path, where the SSR cards are replaced mid-reveal.

  On a filter/sort swap, url-filter-controller replaces the whole grid section
  with fresh hidden cards and dispatches `urlfilter:change`. We re-observe those
  cards so they replay the staggered reveal, matching the first-load entrance.
*/
(function () {
  'use strict';

  const STEP_MS = 90; // delay between cards revealing within one batch
  const BATCH_GAP_MS = 220; // scroll-idle gap that resets the stagger counter
  /* Both grids carry data-searchanise-grid-list since e64efee, so the old
     :not() exclusion matched nothing and this module was dead. */
  const COLLECTION_LIST = '.c-plp-grid__list';
  const URL_FILTER_EVENT = 'urlfilter:change';

  const state = {
    observer: null,
    batchCount: 0,
    lastRevealAt: 0
  };

  function now() {
    return (window.performance && performance.now) ? performance.now() : Date.now();
  }

  function reveal(card, delayMs) {
    card.style.setProperty('--reveal-delay', delayMs + 'ms');
    card.setAttribute('data-revealed', 'true');
  }

  function revealAllImmediate(list) {
    const cards = list.querySelectorAll('.c-product-card:not([data-revealed="true"])');
    cards.forEach(function (card) {
      reveal(card, 0);
    });
  }

  function onIntersect(entries) {
    // Reveal in reading order so the stagger cascades top-left -> bottom-right;
    // IntersectionObserver does not guarantee entry order.
    const visible = entries.filter(function (entry) {
      return entry.isIntersecting;
    });
    visible.sort(function (a, b) {
      const ra = a.boundingClientRect;
      const rb = b.boundingClientRect;
      return (ra.top - rb.top) || (ra.left - rb.left);
    });
    visible.forEach(function (entry) {
      const at = now();
      if (at - state.lastRevealAt > BATCH_GAP_MS) {
        state.batchCount = 0;
      }
      reveal(entry.target, state.batchCount * STEP_MS);
      state.batchCount += 1;
      state.lastRevealAt = at;
      state.observer.unobserve(entry.target);
    });
  }

  function ensureObserver() {
    if (!state.observer && 'IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(onIntersect, {
        threshold: 0.15,
        rootMargin: '0px 0px -5% 0px'
      });
    }
    return state.observer;
  }

  // Stagger the not-yet-revealed cards in `list` as they scroll into view.
  // Falls back to revealing all immediately where IntersectionObserver is
  // unavailable. Called by plp-searchanise-controller for the cards it builds
  // and for the SSR cards it leaves behind when it declines, and by the
  // urlfilter:change listener below after a native swap.
  function observe(list) {
    if (!list) return;
    if (!ensureObserver()) {
      revealAllImmediate(list);
      return;
    }
    const cards = list.querySelectorAll('.c-product-card:not([data-revealed="true"])');
    cards.forEach(function (card) {
      state.observer.observe(card);
    });
  }

  window.PlpCardReveal = {
    observe: observe,
    revealAll: revealAllImmediate
  };

  function init() {
    // Collection filter/sort swap replaces the grid with fresh hidden cards.
    // Replay the staggered reveal on them (same entrance as first load), but
    // only once the swap settles: url-filter-controller keeps .is-rerendering
    // on the section for two frames and product-list-page.css blanket-suppresses
    // transitions during that window. We rAF-poll until it lifts, then reveal.
    // Waiting (vs forcing the transition) also keeps reduced-motion users on
    // their no-transition rule.
    document.addEventListener(URL_FILTER_EVENT, function () {
      let frames = 0;
      (function revealWhenSettled() {
        const fresh = document.querySelector(COLLECTION_LIST);
        if (!fresh) return;
        if (fresh.closest('.is-rerendering') && frames < 10) {
          frames += 1;
          requestAnimationFrame(revealWhenSettled);
          return;
        }
        // Reset the stagger so the new grid cascades from its first card.
        state.batchCount = 0;
        state.lastRevealAt = 0;
        observe(fresh);
      })();
    });
  }

  document.addEventListener('DOMContentLoaded', init);
})();
